import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/shared/components/ui/Modal';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Button } from '@/shared/components/ui/Button';
import { useToast } from '@/shared/components/ui/Toast';
import { useCreateManufacturer, useUpdateManufacturer } from '../hooks/useAdmin';
import type { Manufacturer } from '../services/manufacturersService';

const manufacturerSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  brands: z.string().optional(),
  min_order_qty: z.number().nullable(),
  min_order_amt: z.number().nullable(),
  free_ship_amt: z.number().nullable(),
  contact_phone: z.string().optional(),
  contact_email: z.string().email('Invalid email').optional().or(z.literal('')),
  rep_name: z.string().optional(),
  rep_phone: z.string().optional(),
  notes: z.string().optional(),
  is_active: z.boolean(),
});

type ManufacturerFormData = z.infer<typeof manufacturerSchema>;

interface ManufacturerFormProps {
  isOpen: boolean;
  onClose: () => void;
  manufacturer?: Manufacturer;
}

export function ManufacturerForm({
  isOpen,
  onClose,
  manufacturer,
}: ManufacturerFormProps) {
  const { showToast } = useToast();
  const isEditing = !!manufacturer;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ManufacturerFormData>({
    resolver: zodResolver(manufacturerSchema),
  });

  const createMutation = useCreateManufacturer();
  const updateMutation = useUpdateManufacturer();

  useEffect(() => {
    if (isOpen) {
      if (manufacturer) {
        reset({
          name: manufacturer.name,
          brands: manufacturer.brands?.join(', ') || '',
          min_order_qty: manufacturer.min_order_qty,
          min_order_amt: manufacturer.min_order_amt,
          free_ship_amt: manufacturer.free_ship_amt,
          contact_phone: manufacturer.contact_phone || '',
          contact_email: manufacturer.contact_email || '',
          rep_name: manufacturer.rep_name || '',
          rep_phone: manufacturer.rep_phone || '',
          notes: manufacturer.notes || '',
          is_active: manufacturer.is_active,
        });
      } else {
        reset({
          name: '',
          brands: '',
          min_order_qty: null,
          min_order_amt: null,
          free_ship_amt: null,
          contact_phone: '',
          contact_email: '',
          rep_name: '',
          rep_phone: '',
          notes: '',
          is_active: true,
        });
      }
    }
  }, [isOpen, manufacturer, reset]);

  const onSubmit = async (data: ManufacturerFormData) => {
    try {
      // Parse brands from comma-separated string to array
      const brands = data.brands
        ? data.brands
            .split(',')
            .map((b) => b.trim())
            .filter((b) => b.length > 0)
        : [];

      const payload = {
        name: data.name,
        brands: brands.length > 0 ? brands : undefined,
        min_order_qty: data.min_order_qty || undefined,
        min_order_amt: data.min_order_amt || undefined,
        free_ship_amt: data.free_ship_amt || undefined,
        contact_phone: data.contact_phone || undefined,
        contact_email: data.contact_email || undefined,
        rep_name: data.rep_name || undefined,
        rep_phone: data.rep_phone || undefined,
        notes: data.notes || undefined,
        is_active: data.is_active,
      };

      if (isEditing && manufacturer) {
        await updateMutation.mutateAsync({
          id: manufacturer.id,
          ...payload,
        });
        showToast('Manufacturer updated successfully', 'success');
      } else {
        await createMutation.mutateAsync(payload);
        showToast('Manufacturer created successfully', 'success');
      }
      onClose();
      reset();
    } catch (error) {
      console.error('Error saving manufacturer:', error);
      showToast(
        error instanceof Error ? error.message : 'Failed to save manufacturer',
        'error'
      );
    }
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Edit Manufacturer' : 'Add New Manufacturer'}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Manufacturer Name"
          required
          placeholder="e.g., Ray-Ban"
          error={errors.name?.message}
          {...register('name')}
        />

        <Input
          label="Brands"
          placeholder="e.g., Ray-Ban, Oakley (comma separated)"
          error={errors.brands?.message}
          {...register('brands')}
          helperText="Enter brand names separated by commas. Each brand will display as a badge in the table."
        />

        <div className="border-t pt-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            Shipping & Ordering Requirements
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Min Qty for Free Ship (frames)"
              type="number"
              placeholder="0"
              error={errors.min_order_qty?.message}
              {...register('min_order_qty', { valueAsNumber: true })}
              helperText="Minimum number of frames to qualify for free shipping"
            />
            <Input
              label="Free Ship $ Threshold"
              type="number"
              step="0.01"
              placeholder="0.00"
              error={errors.free_ship_amt?.message}
              {...register('free_ship_amt', { valueAsNumber: true })}
              helperText="Dollar amount to qualify for free shipping"
            />
          </div>
        </div>

        <div className="border-t pt-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            Contact Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Contact Phone"
              type="tel"
              placeholder="(555) 123-4567"
              error={errors.contact_phone?.message}
              {...register('contact_phone')}
            />
            <Input
              label="Contact Email"
              type="email"
              placeholder="contact@manufacturer.com"
              error={errors.contact_email?.message}
              {...register('contact_email')}
            />
          </div>
        </div>

        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900">
              Sales Representative
            </h3>
            <span className="text-xs text-gray-400 italic">
              Brand-level reps coming in Phase 2
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Rep Name"
              placeholder="John Doe"
              error={errors.rep_name?.message}
              {...register('rep_name')}
              helperText="Manufacturer-level sales rep"
            />
            <Input
              label="Rep Phone"
              type="tel"
              placeholder="(555) 987-6543"
              error={errors.rep_phone?.message}
              {...register('rep_phone')}
            />
          </div>
        </div>

        <Textarea
          label="Notes"
          placeholder="Additional notes..."
          rows={3}
          error={errors.notes?.message}
          {...register('notes')}
        />

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_active"
            {...register('is_active')}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
            Active
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
